import { FocusTrap, inertHelper, isMobile, ScrollLock, Swipeable, SwipeDirection } from '@farmasiet/frontendcore-reactcomponents';
import classNames from 'classnames';
import intervalToDuration from 'date-fns/intervalToDuration';
import * as React from 'react';
import { useEffect } from 'react';
import { Collapse } from 'react-collapse';

import { getTranslation, keys } from '../../../Resources/Translations';
import { LinkViewModel } from '../../Common/LinkViewModel.csharp';
import { CookieAccessor } from '../../Customers/UserProfile/Components/CookiesConsent/CookieAccessor';
import { HeaderLinkColor } from '../../Partials/Header/HeaderLinkColor.csharp';
import { HeaderLinkIcon } from '../../Partials/Header/HeaderLinkIcon.csharp';
import { RxHeaderLink } from '../../Partials/Header/RxHeaderLink.csharp';
import { RxLinkType } from '../../Partials/Header/RxLinkType.csharp';
import { showRxSessionExpiredModal } from '../../Purchase/Rx/Modals/RxSessionExpiredModal';
import { RxDetailedMaintenanceModel } from '../../Rx/RxDetailedMaintenanceModel.csharp';
import { useBankIdSession } from '../ActionMenu/useBankIdSession';
import { NavigationTrackerButton } from '../NavigationTracker/NavigationTrackerButton';
import { NavigationTrackerLink } from '../NavigationTracker/NavigationTrackerLink';
import { BankIdSessionIndicatorProgressBar } from './BankIdSessionIndicator/BankIdSessionIndicatorProgressBar/BankIdSessionIndicatorProgressBar';
import { BankIdSessionIndicatorTimer } from './BankIdSessionIndicator/BankIdSessionIndicatorTimer/BankIdSessionIndicatorTimer';
import { LogoutFromBankIdMenuSection } from './LogoutFromBankIdMenuSection/LogoutFromBankIdMenuSection';

interface RxMenuProps {
    isActive: boolean;
    setRxMenuOpen: (value: boolean) => void;
    rxMenuTitle: string;
    rxMenuTitleActiveBankIdSession: string;
    rxLinks: RxHeaderLink[];
    articlesLinks: LinkViewModel[];
    rxSessionCookieName: string;
    rxSessionValidityCookieName: string;
    rxSessionExpirationTimeCookieName: string;
    rxSessionValidityDefaultTimeInMinutes: number;
    rxSessionRedIndicatorThresholdInMinutes: number;
    unreadMessagesBadgeUrl: string;
    customerSupportLink: string;
    customerServiceLabel?: string;
    customerServicePhoneNumber: string;
    showButtonLabel: boolean;
    buttonLabel: string;
    prescriptionListPageUrl: string;
    buyPrescriptionButtonText: string;
    maintenance: RxDetailedMaintenanceModel;
}

export const RxMenu = (props: RxMenuProps) => {
    const { isActive, setRxMenuOpen, unreadMessagesBadgeUrl, showButtonLabel, buttonLabel } = props;
    const [isMobileVersion, setIsMobileVersion] = React.useState(isMobile());
    const [durationToExpiry, setDurationToExpiry] = React.useState<Duration | undefined>(undefined);
    const [bankIdSessionCookie, setBankIdSessionCookie] = React.useState<string | null>(null);

    const bankIdSessionExpiration = React.useRef(0);
    const rxMenuWrapperRef = React.useRef<HTMLDivElement>(null);
    const intervalRef = React.useRef<NodeJS.Timer>();

    const maintenance = {
        [RxLinkType.PrescriptionList]: props.maintenance.prescriptionListMaintenance,
        [RxLinkType.PrescriptionHistory]: props.maintenance.prescriptionHistoryMaintenance,
        [RxLinkType.Inbox]: props.maintenance.inboxMaintenance
    };

    const bankIdSession = useBankIdSession(props.rxSessionCookieName, props.rxSessionValidityCookieName, props.rxSessionExpirationTimeCookieName);

    const calculateDurationToExpiry = () => {
        const dateNow = Date.now();
        const dateInterval = bankIdSessionExpiration.current - dateNow;

        if (dateInterval > 0) {
            setDurationToExpiry(intervalToDuration({ start: 0, end: dateInterval }));
            return;
        }

        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }

        showRxSessionExpiredModal();
    };

    useEffect(() => {
        setBankIdSessionCookie(CookieAccessor.getCookie(props.rxSessionExpirationTimeCookieName));
    }, []);

    useEffect(() => {
        if (bankIdSessionCookie) {
            bankIdSessionExpiration.current = new Date(decodeURIComponent(bankIdSessionCookie)).getTime();
            intervalRef.current = setInterval(() => calculateDurationToExpiry(), 1000);

            return () => {
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                }
            };
        }
    }, [bankIdSessionCookie]);

    const resizeEventHandler = () => {
        setIsMobileVersion(isMobile());
    };

    useEffect(() => {
        window?.addEventListener('resize', resizeEventHandler);

        return () => {
            window?.removeEventListener('resize', resizeEventHandler);
        };
    }, []);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (rxMenuWrapperRef.current && !rxMenuWrapperRef.current.contains(e.target as Node) && isActive) {
                setRxMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [rxMenuWrapperRef, isActive]);

    const handleSwipe = (swipeDirection: SwipeDirection) => {
        if (swipeDirection === SwipeDirection.Left && isActive) {
            setRxMenuOpen(false);
        }
    };

    const unreadMessagesBadge = (
        <div className="RxMenu__UnreadMessagesBadge">
            <iframe src={unreadMessagesBadgeUrl} width={'100%'} height={'100%'} />
        </div>
    );

    const maintenanceMessage = (
        <div className={classNames('RxMenu__Maintenance', !bankIdSession.isValid && 'RxMenu__Maintenance--LoggedOut')}>
            <div className="RxMenu__MaintenanceImage" />
            <div className="RxMenu__MaintenanceText">
                {props.maintenance.header && <div className="RxMenu__MaintenanceHeader">{props.maintenance.header}</div>}
                {props.maintenance.message && <div className="RxMenu__MaintenanceMessage">{props.maintenance.message}</div>}
            </div>
        </div>
    );

    return (
        <Swipeable onSwipe={handleSwipe}>
            <FocusTrap enabled={isActive}>
                <div ref={rxMenuWrapperRef}>
                    <NavigationTrackerButton
                        className={classNames('Header__ActionsRxPage', { 'Header__ActionsRxPage--LabelVisible': showButtonLabel })}
                        onClick={() => setRxMenuOpen(!isActive)}
                        aria-haspopup="true"
                        aria-expanded={isActive}
                        source="RxMenu"
                    >
                        {showButtonLabel && <span>{buttonLabel}</span>}
                        <span className="visuallyHidden">{getTranslation(keys['actionMenu.rxService'])}</span>
                        {(bankIdSession.isValid && (
                            <BankIdSessionIndicatorTimer
                                durationToExpiry={durationToExpiry}
                                redBackgroundMinutesThreshold={props.rxSessionRedIndicatorThresholdInMinutes}
                            />
                        )) ||
                            (props.maintenance.showMessage && <div className="RxMenu__MaintenanceBadge" />)}
                    </NavigationTrackerButton>
                    <div
                        className={classNames('RxMenu__Overlay', isActive && isMobileVersion && 'RxMenu__Overlay--Active')}
                        onClick={() => setRxMenuOpen(false)}
                    />
                    <div className={classNames('RxMenu', isActive && 'RxMenu--Active')} {...inertHelper(isActive)}>
                        <ScrollLock isLocked={isActive && isMobileVersion} />
                        <Collapse isOpened={isActive}>
                            <div className="RxMenu__Placeholder">
                                <div className="RxMenu__Content">
                                    <div className={classNames('RxMenu__HeaderWrapper', !bankIdSession.isValid && 'RxMenu__HeaderWrapper--HideOnDesktop')}>
                                        {props.rxMenuTitle && (
                                            <div className="RxMenu__Header">
                                                {bankIdSession.isValid ? props.rxMenuTitleActiveBankIdSession : props.rxMenuTitle}
                                            </div>
                                        )}

                                        <button
                                            className="RxMenu__CloseButton"
                                            onClick={() => setRxMenuOpen(false)}
                                            aria-haspopup="true"
                                            aria-expanded={isActive}
                                        >
                                            <span className="visuallyHidden">{getTranslation(keys['navigation.close'])}</span>
                                        </button>
                                    </div>
                                    {props.rxLinks?.length && (
                                        <div className="RxMenu__Section">
                                            <ul className="RxMenu__LinksList">
                                                {bankIdSession.isValid ? (
                                                    <>
                                                        <li key={'bankIdIndicator_progressBar'}>
                                                            <BankIdSessionIndicatorProgressBar
                                                                durationToExpiry={durationToExpiry}
                                                                redBackgroundMinutesThreshold={props.rxSessionRedIndicatorThresholdInMinutes}
                                                                rxSessionValidityDefaultTimeInMinutes={props.rxSessionValidityDefaultTimeInMinutes}
                                                            />
                                                        </li>
                                                        {props.maintenance.showMessage && maintenanceMessage}
                                                    </>
                                                ) : (
                                                    <>
                                                        {props.maintenance.showMessage && maintenanceMessage}
                                                        {!props.maintenance.prescriptionListMaintenance && (
                                                            <li key={props.prescriptionListPageUrl + '_navigationButton'}>
                                                                <NavigationTrackerLink
                                                                    source={'RxMenu'}
                                                                    className={classNames(
                                                                        'Button Button--Primary Button--Medium RxMenu__BuyPrescriptionButton'
                                                                    )}
                                                                    href={props.prescriptionListPageUrl}
                                                                >
                                                                    {props.buyPrescriptionButtonText ?? getTranslation(keys['navigation.buyPrescription'])}
                                                                </NavigationTrackerLink>
                                                            </li>
                                                        )}
                                                    </>
                                                )}

                                                {props.rxLinks?.map((link) => (
                                                    <li className="RxMenu__Link" key={link.link}>
                                                        {(maintenance[link.type] && (
                                                            <div className="RxMenu__Link--Disabled">
                                                                {!!link.icon && (
                                                                    <span
                                                                        className={`RxMenuLinks__LinkIcon SvgIcon NavigationLinkIcon--${
                                                                            HeaderLinkIcon[link.icon]
                                                                        }${HeaderLinkColor[link.color]}`}
                                                                    />
                                                                )}
                                                                <span className="RxMenuLinks__Text">{link.text}</span>
                                                                <div className="RxMenuLinks__MaintenanceBadge" />
                                                            </div>
                                                        )) || (
                                                            <NavigationTrackerLink href={link.link} rel="nofollow" source="RxMenu">
                                                                {!!link.icon && (
                                                                    <span
                                                                        className={`RxMenuLinks__LinkIcon SvgIcon NavigationLinkIcon--${
                                                                            HeaderLinkIcon[link.icon]
                                                                        }${HeaderLinkColor[link.color]}`}
                                                                    />
                                                                )}
                                                                <span className="RxMenuLinks__Text">{link.text}</span>
                                                                {link.type === RxLinkType.Inbox && unreadMessagesBadge}
                                                            </NavigationTrackerLink>
                                                        )}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {props.articlesLinks?.length && (
                                        <div className="RxMenu__Section">
                                            <ul className="RxMenu__ArticlesLinks">
                                                {props.articlesLinks?.map((link) => (
                                                    <li className="RxMenu__Link" key={link.href}>
                                                        <NavigationTrackerLink href={link.href} rel="nofollow" source="RxMenu">
                                                            {link.text}
                                                        </NavigationTrackerLink>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    {props.customerServiceLabel && (
                                        <div className="RxMenu__QuickHelp">
                                            <NavigationTrackerLink className="RxMenu__QuickHelp__Link" href={props.customerSupportLink} source="RxMenu">
                                                <span className={`RxMenu__LinkIcon RxMenuIcon__Support`} />
                                                <div className="RxMenu__TextContainer">
                                                    <span className="RxMenu_Text">{props.customerServiceLabel}</span>
                                                    <span className="RxMenu_Text">{props.customerServicePhoneNumber}</span>
                                                </div>
                                            </NavigationTrackerLink>
                                        </div>
                                    )}
                                    {bankIdSession.isValid && <LogoutFromBankIdMenuSection />}
                                </div>
                            </div>
                        </Collapse>
                    </div>
                </div>
            </FocusTrap>
        </Swipeable>
    );
};
